// 報價單編輯器 — 建立/編輯/轉訂單
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, auth, money, todayStr, addDays, CATEGORIES, r2 } from '../api.js';
import { Modal, Field, Loading, PageHead, AddressPicker, ItemsEditor, useToast } from '../ui.jsx';
import CustomerEditor from '../CustomerEditor.jsx';
import { QuotationDoc } from '../docs.jsx';
import { DocPreview } from '../docs.jsx';
import * as pdfjsLib from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

const emptyItem = () => ({ item_id: null, code: '', description: '', qty: 1, unit: '項', unit_price: 0 });

export default function QuotationEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { toast } = useToast();
  const isNew = !id;
  const [loading, setLoading] = useState(!isNew);
  const [customers, setCustomers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [terms, setTerms] = useState([]);
  const [remarkTpls, setRemarkTpls] = useState([]);
  const [settings, setSettings] = useState({});
  const [q, setQ] = useState(null); // quotation object
  const [convert, setConvert] = useState(null); // convert modal state
  const [saving, setSaving] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [pdfs, setPdfs] = useState([]);
  const [viewPdf, setViewPdf] = useState(null); // 附件檢視器
  const [preview, setPreview] = useState(false);
  const [newCust, setNewCust] = useState(false);
  const [tab, setTab] = useState('main');
  const [cost, setCost] = useState({ labour_hrs: 0, labour_cost: 0, sub_air: 0, sub_water: 0, sub_control: 0, sub_cladding: 0, sub_other: 0, material_cost: 0, contingency_pct: 3, margin_target_pct: 20 });
  const [versions, setVersions] = useState([]);
  const [flash, setFlash] = useState(null); // 必填缺失紅框閃 ('customer'|'address'|'items')
  const loadCost = (qid) => api.get('/quotations/' + qid + '/cost').then(setCost).catch(() => {});
  const loadVersions = (qid) => api.get('/quotations/' + qid + '/versions').then(setVersions).catch(() => setVersions([]));
  const uploadTenderPdf = async (vid, file) => {
    const rd = new FileReader();
    rd.onload = async () => {
      try { await api.post('/versions/' + vid + '/pdf', { filename: file.name, mime: file.type, data_base64: rd.result, port_in: true }); toast('Tender PDF 已存入版本 ✔'); if (id) loadVersions(id); }
      catch (e) { toast(e.message, 'err'); }
    };
    rd.readAsDataURL(file);
  };
  const saveCost = async (qid) => {
    try { await api.put('/quotations/' + qid + '/cost', cost); toast('成本估算已儲存 ✔'); } catch (e) { toast(e.message, 'err'); }
  };

  useEffect(() => {
    api.get('/customers').then(setCustomers);
    api.get('/items').then(setCatalog);
    api.get('/payment-terms').then(setTerms);
    api.get('/remark-templates').then(setRemarkTpls);
    api.get('/settings').then(setSettings);
    if (!isNew) {
      api.get(`/quotations/${id}`).then(d => {
        setQ({ ...d, remarks: (d.remarks || []).map(t => ({ text: t, checked: true })), items: d.items.map(it => ({ ...it, cost_price: it.cost_price || 0 })), manual_total: d.manual_total == null ? '' : d.manual_total });
        setPdfs(d.pdfs || []);
        setLoading(false);
        loadCost(d.id); loadVersions(d.id);
      }).catch(e => { toast(e.message, 'err'); nav('/quotations'); });
    }
  }, [id]);

  const init = useMemo(() => ({
    customer_id: '', address_id: '', category: 'INSTALLATION', title: '',
    recipient: '', office_address: '', quote_no: '', manual_total: '',
    issue_date: todayStr(), valid_until: addDays(todayStr(), Number(settings.default_valid_days) || 30),
    discount_type: 'none', discount_value: 0, payment_term_id: '', notes: '',
    items: [emptyItem()],
    remarks: [],
    status: 'draft',
  }), [settings.default_valid_days]);

  useEffect(() => { if (isNew && !q) setQ({ ...init }); }, [isNew, q, init]);

  // 揀咗客戶 → 載入地址 + 自動帶入收信人/Office 地址 (跟客戶紀錄, 可逐單改)
  useEffect(() => {
    const cid = q?.customer_id;
    if (cid) {
      api.get(`/customers/${cid}`).then(c => {
        const list = c.addresses || [];
        setAddresses(list);
        const def = list.find(a => a.is_default) || list[0];
        setQ(x => {
          if (!x || Number(x.customer_id) !== Number(cid)) return x;
          const stillValid = list.some(a => a.id === Number(x.address_id));
          return {
            ...x,
            address_id: stillValid ? x.address_id : (def ? def.id : null),
            recipient: (x.recipient || '') === '' || x._recipientFrom === 'auto' ? (c.contact || '') : x.recipient,
            office_address: (x.office_address || '') === '' || x._officeFrom === 'auto' ? (c.office_address || '') : x.office_address,
            _recipientFrom: 'auto', _officeFrom: 'auto',
          };
        });
      });
    } else setAddresses([]);
  }, [q?.customer_id]);

  if (loading || !q) return <Loading />;

  const set = (k, v) => setQ(x => ({ ...x, [k]: v }));
  const customer = customers.find(c => c.id === Number(q.customer_id));
  const isAdmin = auth.user?.role === 'admin';

  const subtotal = q.items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unit_price) || 0), 0);
  const discount = q.discount_type === 'percent' ? r2(subtotal * (Number(q.discount_value) || 0) / 100)
    : q.discount_type === 'amount' ? r2(Number(q.discount_value) || 0) : 0;
  const computedTotal = r2(subtotal - Math.min(discount, subtotal));
  // TOTAL AMOUNT 手動覆蓋 (Tender port-in — 填咗就蓋過項目計算)
  const manualTotal = (q.manual_total === '' || q.manual_total == null || isNaN(Number(q.manual_total))) ? null : r2(Number(q.manual_total));
  const total = manualTotal != null ? manualTotal : computedTotal;

  const toggleRemark = (text) => setQ(x => ({
    ...x,
    remarks: x.remarks.some(r => r.text === text)
      ? x.remarks.filter(r => r.text !== text)
      : [...x.remarks, { text, checked: true }],
  }));

  // 必填完成度 — 常駐顯示, 唔靠一閃即逝嘅 toast (地址可選, 唔強制; 有手動總額可以冇項目 — Tender port-in)
  const missing = {
    customer: !q.customer_id,
    items: !q.items.some(it => (it.description || '').trim()) && manualTotal == null,
  };
  const missingCount = Object.values(missing).filter(Boolean).length;
  const flagMissing = () => {
    const first = missing.customer ? 'customer' : 'items';
    setFlash(first);
    setTimeout(() => setFlash(null), 2600);
  };

  const save = async (opts = {}) => {
    const { thenConvert = false, stay = false } = typeof opts === 'boolean' ? { thenConvert: opts } : opts;
    if (missingCount) { flagMissing(); toast('未儲存 — 仲有 ' + missingCount + ' 項必填未完成 (睇上方檢查條)', 'err'); return null; }
      const items = q.items.filter(it => (it.description || '').trim());
      setSaving(true);
      try {
        const effId = q.id || id;
        const body = { ...q, items, remarks: q.remarks.filter(r => r.checked !== false).map(r => r.text), subtotal, total };
        delete body._recipientFrom; delete body._officeFrom;
      const r = !effId ? await api.post('/quotations', body) : await api.put(`/quotations/${effId}`, body);
      const nid = r.id || Number(effId);
      toast('報價單已儲存 ✔');
      if (!q.id && nid) setQ(x => ({ ...x, id: nid }));
      if (thenConvert) return nid;
      if (!stay) nav(`/quotations/${nid}`);
      return nid;
    } catch (e) { toast(e.message, 'err'); return null; } finally { setSaving(false); }
  };

  const markStatus = async (status, msg) => {
    await save();
    try { await api.post(`/quotations/${id || q.id}/status`, { status }); toast(msg); set('status', status); } catch (e) { toast(e.message, 'err'); }
  };

  // 附件 PDF 上傳 (base64 JSON — 同 PO 附件一樣模式)
  const uploadPdf = (file) => {
    const rd = new FileReader();
    rd.onload = async () => {
      try {
        const r = await api.post(`/quotations/${q.id}/pdfs`, { filename: file.name, mime: file.type, data_base64: rd.result });
        setPdfs(x => [...x, r]);
        toast('附件已上傳 ✔');
      } catch (e) { toast(e.message, 'err'); }
    };
    rd.readAsDataURL(file);
  };
  const delPdf = async (p) => {
    try { await api.del(`/quotations/${q.id}/pdfs/${p.id}`); setPdfs(x => x.filter(z => z.id !== p.id)); toast('附件已刪除'); }
    catch (e) { toast(e.message, 'err'); }
  };

  const buildDocData = () => {
    const term = terms.find(t => t.id === Number(q.payment_term_id));
    return {
      ...q,
      items: q.items.filter(it => it.description.trim()),
      customer_name: customer?.name, subtotal, total,
      payment_term_name: term?.name,
      payment_installments: term?.installments || [],
      remarks: q.remarks.filter(r => r.checked !== false).map(r => r.text),
    };
  };

  // PDF 只喺預覽入面出 — 預覽前一定已經儲存咗 (qid 一定存在)
  const toPDF = async () => {
    const qid = q.id || id;
    if (!qid) { toast('請先儲存報價單', 'err'); return; }
    try {
      toast('正在產生 PDF…');
      const resp = await fetch('/api/quotations/' + qid + '/generate-pdf', { headers: { Authorization: 'Bearer ' + auth.token } });
      if (!resp.ok) throw new Error('PDF 生成失敗 (' + resp.status + ')');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = (q.quote_no || 'quotation') + '.pdf';
      a.click();
      URL.revokeObjectURL(url);
      toast('PDF 已下載 ✔');
    } catch (e) { toast(e.message, 'err'); }
  };

  // 預覽 = 內容最後把關: 新單先自動儲存 (唔跳頁), 再開預覽; PDF 掣喺預覽入面
  const openPreview = async () => {
    if (!q.id && !id) {
      const nid = await save({ stay: true });
      if (!nid) { flagMissing(); return; }
      window.history.replaceState({}, '', '/quotations/' + nid);
    }
    setPreview(true);
  };

  const openConvert = async () => {
    const effId = q.id || id;
    let nid = effId;
    if (!nid) {
      nid = await save({ stay: true });
      if (!nid) return;
      window.history.replaceState({}, '', '/quotations/' + nid);
    }
    const fresh = await api.get(`/quotations/${nid}`);
    setConvert({
      order_date: todayStr(), completion_date: '',
      discount_type: fresh.discount_type, discount_value: fresh.discount_value,
      payment_term_id: fresh.payment_term_id || '', notes: fresh.notes || '',
      remarks: Array.isArray(fresh.remarks) ? fresh.remarks : JSON.parse(fresh.remarks || '[]'),
      items: fresh.items.map(it => ({ ...it })),
      subtotal: fresh.subtotal, total: fresh.total,
    });
  };

  const locked = !isNew && q && q.status === 'converted'; // 已轉單 → 唯讀, 只可檢視 / 出 PDF

  return (
    <div>
      <PageHead icon="📄" title={isNew ? '➕ 新增報價單' : `報價單 ${q.quote_no}`}
        desc={isNew ? 'INSTALLATION · MAINTENANCE · E-CALL 三大類' : `${customer?.name || ''} — 建立於 ${q.issue_date}`}
        actions={<>
          <button className="btn" onClick={() => nav('/quotations')}>← 返回</button>
          <button className="btn" onClick={openPreview}>👁 預覽</button>
          {!isNew && q.status === 'draft' && <button className="btn amber" onClick={() => markStatus('sent', '已標記為「已發出」')}>📤 標記已發出</button>}
          {!isNew && q.status === 'sent' && <button className="btn mint" onClick={() => markStatus('accepted', '已標記為「已接受」')}>🤝 客已接受</button>}
          {!isNew && ['draft', 'sent', 'accepted'].includes(q.status) && <button className="btn violet" onClick={openConvert}>🏗️ 轉訂單</button>}
          {!isNew && !locked && <button className="btn" onClick={async () => {
            try { const r = await api.post(`/quotations/${q.id || id}/revision`); toast('已開 Revision: ' + r.quote_no + ' ✔'); nav('/quotations/' + r.id); }
            catch (e) { toast(e.message, 'err'); }
          }} title="基於呢張開修訂版 (RA/RB/RC…) — 複製全部內容, 新編號加後綴">📄 開 Revision</button>}
          {!locked && <button className="btn primary" disabled={saving} onClick={() => save()}>{saving ? '儲存中…' : '💾 儲存'}</button>}
        </>} />

      <div className="tabs">
        <button className={'tab ' + (tab === 'main' ? 'active' : '')} onClick={() => setTab('main')}>📝 報價內容</button>
        <button className={'tab ' + (tab === 'cost' ? 'active' : '')} onClick={() => setTab('cost')}>💰 成本估算</button>
        <button className={'tab ' + (tab === 'versions' ? 'active' : '')} onClick={() => setTab('versions')}>🗂️ 版本管理{versions.length ? ' (' + versions.length + ')' : ''}</button>
      </div>

      {locked && (
        <div className="checklist-bar" style={{ background: '#2b3c47', borderColor: '#607e91' }}>
          <strong>🔒 此報價單已轉為訂單 — 已鎖定唯讀</strong>
          <span className="muted">— 只可檢視 / 出 PDF；要改工程內容請喺訂單開 Variation Order (VO)</span>
          {q.converted_order_id ? <button className="btn sm" onClick={() => nav('/orders/' + q.converted_order_id)}>🏗️ 查看訂單</button> : null}
        </div>
      )}

      {/* 必填檢查條 — 常駐顯示, 未完成一眼見到 (地址可選) */}
      {!locked && missingCount ? (
        <div className="checklist-bar">
          <strong>📋 未完成必填 {missingCount}/2：</strong>
          <span className={'chip ' + (missing.customer ? 'chip-miss' : 'chip-ok')}>{missing.customer ? '✗ 客戶未揀' : '✓ 客戶'}</span>
          <span className={'chip ' + (missing.items ? 'chip-miss' : 'chip-ok')}>{missing.items ? '✗ 未有項目' : '✓ 項目'}</span>
          <span className="chip chip-opt">{q.address_id ? '✓ 地址' : '○ 地址 (可選)'}</span>
          <span className="muted">— 補齊必填後方可儲存 / 預覽 / 出 PDF</span>
        </div>
      ) : !locked ? (
        <div className="checklist-bar ok"><span className="chip chip-ok">✓ 客戶</span><span className="chip chip-ok">✓ 項目</span><span className="chip chip-opt">{q.address_id ? '✓ 地址' : '○ 地址 (可選)'}</span><span className="muted">— 可以儲存 ✔</span></div>
      ) : null}

      {tab !== 'main' ? null : (
      <fieldset disabled={locked} style={{ border: 0, padding: 0, margin: 0, opacity: locked ? 0.72 : 1 }}>
      <>
      <div className="grid2">
        <div className="card" data-field="customer" style={{ outline: flash === 'customer' ? '2px solid var(--coral)' : 'none', outlineOffset: 2 }}>
          <div className="card-title"><span className="ico-badge" style={{ width: 28, height: 28, fontSize: 13, borderRadius: 8 }}>🏢</span>客戶資料</div>
          <div className="form-grid">
            <Field label="客戶" required>
              <div className="row" style={{ flexWrap: 'nowrap' }}>
                <select className="select" style={{ flex: 1 }} value={q.customer_id} onChange={e => set('customer_id', e.target.value)}>
                  <option value="">— 選擇客戶 —</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <button type="button" className="btn primary" style={{ padding: '9px 13px' }}
                  title="即場新增客戶同地址" onClick={() => setNewCust(true)}>➕</button>
              </div>
            </Field>
            <Field label="報價類別" required>
              <div className="pill-filter" style={{ paddingTop: 4 }}>
                {['INSTALLATION', 'MAINTENANCE', 'ECALL'].map(c => (
                  <button key={c} type="button" className={`pill ${q.category === c ? 'active' : ''}`} onClick={() => set('category', c)}>
                    {CATEGORIES[c].icon} {CATEGORIES[c].label}
                  </button>
                ))}
              </div>
            </Field>
            {isAdmin ? (
              <Field label="報價編號 Quote No. (管理員可改)">
                <input className="input" value={q.quote_no || ''} onChange={e => set('quote_no', e.target.value)} placeholder="留空 = 自動編號 (YYMM+序)" />
              </Field>
            ) : null}
            <Field label="項目名稱 (Site)" span2><input className="input" value={q.title} onChange={e => set('title', e.target.value)} placeholder="例如: XX中心升降機年度保養合約" /></Field>
            <Field label="收信人 Recipient (印喺報價單)"><input className="input" value={q.recipient || ''} onChange={e => set('recipient', e.target.value)} placeholder="自動帶入客戶聯絡人 — 可改" /></Field>
            <Field label="客戶地址 Office Address (印喺報價單)"><input className="input" value={q.office_address || ''} onChange={e => set('office_address', e.target.value)} placeholder="自動帶入客戶 Office 地址 — 可改" /></Field>
            <Field label="報價日期"><input className="input" type="date" value={q.issue_date} onChange={e => set('issue_date', e.target.value)} /></Field>
            <Field label="有效期至"><input className="input" type="date" value={q.valid_until} onChange={e => set('valid_until', e.target.value)} /></Field>
          </div>
          {customer ? (
            <div className="mt" style={{ outline: flash === 'address' ? '2px solid var(--coral)' : 'none', outlineOffset: 2, borderRadius: 6 }}>
              <AddressPicker customerId={q.customer_id} addresses={addresses} value={Number(q.address_id) || ''} onChange={v => set('address_id', v)} onNewAddress={a => setAddresses(x => [...x, a])} />
              {q.address_id ? <AddrSaver customerId={q.customer_id} addressId={q.address_id} addresses={addresses}
                onSaved={a => setAddresses(list => list.map(x => x.id === a.id ? a : x))} /> : null}
            </div>
          ) : null}
        </div>

        <div className="card">
          <div className="card-title"><span className="ico-badge amber" style={{ width: 28, height: 28, fontSize: 13, borderRadius: 8 }}>📋</span>折扣 · 付款條款</div>
          <div className="form-grid">
            <Field label="折扣">
              <select className="select" value={q.discount_type} onChange={e => set('discount_type', e.target.value)}>
                <option value="none">無折扣</option>
                <option value="percent">百分比 %</option>
                <option value="amount">固定金額 $</option>
              </select>
            </Field>
            {q.discount_type !== 'none' ? (
              <Field label={q.discount_type === 'percent' ? '折扣 %' : '折扣金額'}>
                <input className="input num-input" type="number" step="0.01" min="0" value={q.discount_value} onChange={e => set('discount_value', e.target.value)} />
              </Field>
            ) : <div />}
            <Field label="付款條款 Payment Term" span2>
              <select className="select" value={q.payment_term_id} onChange={e => set('payment_term_id', e.target.value)}>
                <option value="">— 選擇 —</option>
                {terms.map(t => <option key={t.id} value={t.id}>{t.name} — {t.description}</option>)}
              </select>
            </Field>
            <Field label="TOTAL AMOUNT 總額覆蓋 (Tender port-in 用)" span2>
              <input className="input num-input" type="number" step="0.01" min="0" value={q.manual_total ?? ''} onChange={e => set('manual_total', e.target.value)} placeholder="留空 = 自動按項目計算" />
            </Field>
          </div>
          <div className="total-bar mt">
            <div className="line"><span>小計 Subtotal</span><span className="v">HK$ {money(subtotal)}</span></div>
            {discount > 0 ? <div className="line"><span>折扣 Discount</span><span className="v">- HK$ {money(Math.min(discount, subtotal))}</span></div> : null}
            {manualTotal != null ? <div className="line" style={{ color: 'var(--ink)' }}><span>手動總額覆蓋 Manual Override</span><span className="v">HK$ {money(manualTotal)}</span></div> : null}
            <div className="line grand"><span>總計 TOTAL</span><span className="v">HK$ {money(total)}</span></div>
            {manualTotal != null ? <div className="line"><span className="muted" style={{ fontSize: 12 }}>⚠ 手動總額生效中 — 項目金額計算已被覆蓋</span></div> : null}
          </div>
        </div>
      </div>

      <div className="card mt" style={{ outline: flash === 'items' ? '2px solid var(--coral)' : 'none', outlineOffset: 2 }}>
        <div className="card-title"><span className="ico-badge mint" style={{ width: 28, height: 28, fontSize: 13, borderRadius: 8 }}>📝</span>報價項目 — 手動輸入嘅項目會自動存入項目庫</div>
        <ItemsEditor items={q.items} setItems={v => set('items', v)} itemsCatalog={catalog} showCost />
      </div>

      <div className="card">
        <div className="card-title"><span className="ico-badge coral" style={{ width: 28, height: 28, fontSize: 13, borderRadius: 8 }}>🛡️</span>REMARKS 保障條款 (揀選會印喺報價單上)</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 8 }}>
          {remarkTpls.map(t => {
            const on = q.remarks.some(r => r.text === t.text);
            return (
              <label key={t.id} className="row" style={{
                gap: 8, fontSize: 12.5, padding: '9px 12px', borderRadius: 11, cursor: 'pointer',
                background: on ? 'var(--surface-selected)' : 'var(--surface-soft)', border: `1.5px solid ${on ? 'var(--ink-3)' : 'var(--line)'}`,
                color: 'var(--ink-2)', alignItems: 'flex-start', lineHeight: 1.5,
              }}>
                <input type="checkbox" checked={on} onChange={() => toggleRemark(t.text)} style={{ marginTop: 3 }} />
                <span>{t.text}</span>
              </label>
            );
          })}
        </div>
        <AddRemarkTpl onAdded={(tpl) => {
          api.get('/remark-templates').then(setRemarkTpls);
          if (tpl && !q.remarks.some(r => r.text === tpl)) {
            setQ(x => ({ ...x, remarks: [...x.remarks, { text: tpl, checked: true }] }));
          }
        }} />
      </div>

      {/* ===== 附件 PDF (Tender / 舊報價 port-in) ===== */}
      <div className="card">
        <div className="card-title"><span className="ico-badge sky" style={{ width: 28, height: 28, fontSize: 13, borderRadius: 8 }}>📎</span>附件 PDF — Tender / 舊報價 (port-in 原稿)
          <span className="spacer" />
          {q.id ? (
            <label className="btn primary" style={{ cursor: 'pointer' }}>
              ⬆ 上傳 PDF / 圖片
              <input type="file" accept="application/pdf,image/png,image/jpeg,image/webp" style={{ display: 'none' }}
                onChange={e => { const f = e.target.files[0]; e.target.value = ''; if (f) uploadPdf(f); }} />
            </label>
          ) : null}
        </div>
        {!q.id ? (
          <p className="muted" style={{ padding: 16 }}>新報價單請先撳「💾 儲存」，之後就可以上傳 Tender / 舊報價 PDF 做存檔。</p>
        ) : pdfs.length === 0 ? (
          <p className="muted" style={{ padding: 16 }}>未有附件 — 上傳後可以喺呢度翻閱 (支援左右翻頁)。</p>
        ) : (
          <div>
            {pdfs.map(p => (
              <div key={p.id} className="row" style={{ justifyContent: 'space-between', background: 'var(--surface-soft)', border: '1px solid var(--line)', borderRadius: 13, padding: '11px 14px', marginBottom: 10 }}>
                <div className="row" style={{ gap: 10 }}>
                  <span>{p.mime === 'application/pdf' ? '📄' : '🖼️'}</span>
                  <strong>{p.filename}</strong>
                  <span className="muted">{(p.size / 1024 / 1024).toFixed(2)} MB</span>
                </div>
                <div className="row" style={{ flexWrap: 'nowrap' }}>
                  <button className="btn sm" onClick={() => setViewPdf({ ...p, url: '/uploads/' + p.stored_name })}>👁 查看</button>
                  <button className="btn sm coral" onClick={() => delPdf(p)}>🗑️</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      </>
      </fieldset>
      )}

      {/* ===== 成本估算 tab ===== */}
      {tab === 'cost' && (
        <fieldset disabled={locked} style={{ border: 0, padding: 0, margin: 0, opacity: locked ? 0.72 : 1 }}>
        <div className="card">
          <div className="card-title"><span className="ico-badge mint" style={{ width: 28, height: 28, fontSize: 13, borderRadius: 8 }}>💰</span>成本估算 — Job Costing
            <span className="spacer" />
            <button className="btn primary" onClick={() => { if (!id) { toast('請先儲存報價單先可以入成本', 'err'); return; } saveCost(id); }}>💾 儲存成本</button>
          </div>
          {!id ? (
            <p className="muted" style={{ padding: 20 }}>新報價單請先撳「💾 儲存」，之後返嚟呢頁入成本。</p>
          ) : (
            <>
              <div className="form-grid">
                <div className="field"><label>Labour Hours (工時)</label><input className="input num-input" type="number" step="0.5" value={cost.labour_hrs} onChange={e => setCost({ ...cost, labour_hrs: e.target.value })} /></div>
                <div className="field"><label>Labour Cost (HKD$)</label><input className="input num-input" type="number" step="0.01" value={cost.labour_cost} onChange={e => setCost({ ...cost, labour_cost: e.target.value })} /></div>
              </div>
              <p className="muted mt mb" style={{ fontWeight: 800 }}>Subcontract 分判:</p>
              <div className="form-grid">
                <div className="field"><label>a) Air Side</label><input className="input num-input" type="number" step="0.01" value={cost.sub_air} onChange={e => setCost({ ...cost, sub_air: e.target.value })} /></div>
                <div className="field"><label>b) Water Side</label><input className="input num-input" type="number" step="0.01" value={cost.sub_water} onChange={e => setCost({ ...cost, sub_water: e.target.value })} /></div>
                <div className="field"><label>c) Control</label><input className="input num-input" type="number" step="0.01" value={cost.sub_control} onChange={e => setCost({ ...cost, sub_control: e.target.value })} /></div>
                <div className="field"><label>d) Cladding / Insulation</label><input className="input num-input" type="number" step="0.01" value={cost.sub_cladding} onChange={e => setCost({ ...cost, sub_cladding: e.target.value })} /></div>
                <div className="field"><label>e) Other</label><input className="input num-input" type="number" step="0.01" value={cost.sub_other} onChange={e => setCost({ ...cost, sub_other: e.target.value })} /></div>
              </div>
              <p className="muted mt mb" style={{ fontWeight: 800 }}>材料及其他:</p>
              <div className="form-grid">
                <div className="field"><label>Material Cost (HKD$)</label><input className="input num-input" type="number" step="0.01" value={cost.material_cost} onChange={e => setCost({ ...cost, material_cost: e.target.value })} /></div>
                <div className="field"><label>Contingency 預備費 %</label><input className="input num-input" type="number" step="0.5" value={cost.contingency_pct} onChange={e => setCost({ ...cost, contingency_pct: e.target.value })} /></div>
                <div className="field"><label>Margin Target 目標利潤 %</label><input className="input num-input" type="number" step="1" value={cost.margin_target_pct} onChange={e => setCost({ ...cost, margin_target_pct: e.target.value })} /></div>
              </div>
              {(() => {
                const base = (Number(cost.labour_cost)||0) + (Number(cost.sub_air)||0) + (Number(cost.sub_water)||0) + (Number(cost.sub_control)||0) + (Number(cost.sub_cladding)||0) + (Number(cost.sub_other)||0) + (Number(cost.material_cost)||0);
                const totalCost = Math.round(base * (1 + (Number(cost.contingency_pct)||0)/100) * 100) / 100;
                const estProfit = Math.round((total - totalCost) * 100) / 100;
                const estMargin = totalCost > 0 ? Math.round(estProfit / totalCost * 1000) / 10 : null;
                const onTarget = estMargin !== null && estMargin >= (Number(cost.margin_target_pct)||0);
                return (
                  <div className="total-bar mt">
                    <div className="line"><span>直接成本 Direct Cost</span><span className="v">HK$ {money(base)}</span></div>
                    <div className="line"><span>Contingency ({cost.contingency_pct || 0}%)</span><span className="v">HK$ {money(totalCost - base)}</span></div>
                    <div className="line"><span>總成本 Total Cost</span><span className="v">HK$ {money(totalCost)}</span></div>
                    <div className="line"><span>報價總額 Quotation Total</span><span className="v">HK$ {money(total)}</span></div>
                    <div className="line grand"><span>預計利潤 Est. Profit</span><span className="v" style={{ color: estProfit >= 0 ? 'var(--mint)' : 'var(--coral)' }}>HK$ {money(estProfit)}{estMargin !== null ? ' (' + estMargin + '%)' : ''}</span></div>
                    <div className="line"><span>目標 Margin Target</span><span className="v">{onTarget ? '✅ 達標' : '⚠️ 低於目標 ' + cost.margin_target_pct + '%'}</span></div>
                  </div>
                );
              })()}
            </>
          )}
        </div>
        </fieldset>
      )}

      {/* ===== 版本管理 tab ===== */}
      {tab === 'versions' && (
        <div className="card">
          <div className="card-title"><span className="ico-badge coral" style={{ width: 28, height: 28, fontSize: 13, borderRadius: 8 }}>🗂️</span>版本管理 — RA / RB / RC + Tender PDF port-in
            <span className="spacer" />
            <button className="btn primary" onClick={async () => {
              if (!id) { toast('請先儲存報價單', 'err'); return; }
              try { await api.post('/quotations/' + id + '/versions', {}); toast('新版本已建立 ✔'); loadVersions(id); } catch (e) { toast(e.message, 'err'); }
            }}>➕ 建立新版本</button>
          </div>
          {!id ? <p className="muted" style={{ padding: 20 }}>新報價單請先儲存。</p> : versions.length === 0 ? (
            <p className="muted" style={{ padding: 20 }}>未有版本 — 撳「建立新版本」會自動編號 RA → RB → RC。每個版本可以上傳 Tender PDF 做存檔 (port-in)。</p>
          ) : (
            <div>
              {versions.map(v => (
                <div key={v.id} className="row" style={{ justifyContent: 'space-between', background: 'var(--surface-soft)', border: '1px solid var(--line)', borderRadius: 13, padding: '12px 14px', marginBottom: 10 }}>
                  <div className="row" style={{ gap: 10 }}>
                    <span className="count-pill" style={{ fontSize: 13 }}>{v.version}</span>
                    {v.port_in ? <span className="st st-gray">Tender port-in</span> : null}
                    <span className="muted">{(v.created_at || '').slice(0, 10)}</span>
                  </div>
                  <div className="row" style={{ flexWrap: 'nowrap' }}>
                    <label className="btn sm" style={{ cursor: 'pointer' }}>
                      📎 上傳 PDF
                      <input type="file" accept="application/pdf,image/*" style={{ display: 'none' }}
                        onChange={e => { const f = e.target.files[0]; e.target.value = ''; if (f) uploadTenderPdf(v.id, f); }} />
                    </label>
                    {v.pdf_file ? <a className="btn sm" href={'/api/versions/' + v.id + '/pdf'}>📄 查看</a> : null}
                    <button className="btn sm coral" onClick={async () => { try { await api.del('/versions/' + v.id); loadVersions(id); toast('已刪除'); } catch (e) { toast(e.message, 'err'); } }}>🗑️</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {convert && <ConvertModal q={q} data={convert} setData={setConvert} terms={terms} settings={settings}
        onClose={() => setConvert(null)}
        onDone={(oid) => { setConvert(null); nav(`/orders/${oid}`); }} />}

      {preview && (
        <DocPreview title={`預覽 — ${q.quote_no || '新報價單'}`} onClose={() => setPreview(false)} onPDF={toPDF}>
          <QuotationDoc data={buildDocData()} settings={settings} />
        </DocPreview>
      )}

      {viewPdf && <PdfViewer pdf={viewPdf} onClose={() => setViewPdf(null)} />}

      {newCust && (
        <CustomerEditor initial={{}}
          onClose={() => setNewCust(false)}
          onSaved={async (newId) => {
            setNewCust(false);
            const list = await api.get('/customers');
            setCustomers(list);
            setQ(x => ({ ...x, customer_id: newId }));
            const c = await api.get(`/customers/${newId}`);
            setAddresses(c.addresses || []);
            const def = (c.addresses || []).find(a => a.is_default) || (c.addresses || [])[0];
            if (def) setQ(x => ({ ...x, address_id: def.id }));
            toast('已選用新客戶 ✔');
          }} />
      )}

      {/* 底部功能列 */}
      <div className="action-dock">
        <button className="btn" onClick={() => nav('/dashboard')}>🏠 主頁</button>
        <div className="spacer" style={{ flex: 1 }} />
        <button className="btn" onClick={openPreview}>👁 預覽</button>
        {!locked && <button className="btn primary" disabled={saving} onClick={() => save()}>{saving ? '儲存中…' : '💾 儲存'}</button>}
      </div>
    </div>
  );
}

// 地址即場修改 — 改完存返入客戶資料庫
function AddrSaver({ customerId, addressId, addresses, onSaved }) {
  const { toast } = useToast();
  const cur = addresses.find(a => a.id === Number(addressId));
  const [f, setF] = useState({ label: cur?.label || '', address: cur?.address || '' });
  useEffect(() => { setF({ label: cur?.label || '', address: cur?.address || '' }); }, [addressId]);
  if (!cur) return null;
  const dirty = (f.label || '') !== (cur.label || '') || (f.address || '') !== (cur.address || '');
  const save = async () => {
    if (!f.address.trim()) { toast('請填地址', 'err'); return; }
    try {
      await api.put(`/customers/${customerId}/addresses/${addressId}`, { label: f.label, address: f.address, is_default: !!cur.is_default });
      toast('地址已存回客戶資料庫 ✔');
      onSaved({ ...cur, label: f.label, address: f.address });
    } catch (e) { toast(e.message, 'err'); }
  };
  return (
    <div className="row" style={{ marginTop: 8, background: 'var(--surface-soft)', padding: 10, borderRadius: 10, border: '1px dashed var(--line)', alignItems: 'center', flexWrap: 'nowrap' }}>
      <input className="input" style={{ flex: '0 0 150px' }} placeholder="標籤 (例如: 地盤B)" value={f.label} onChange={e => setF(x => ({ ...x, label: e.target.value }))} />
      <input className="input" style={{ flex: 1 }} placeholder="地址 — 有更改可存回客戶資料庫" value={f.address} onChange={e => setF(x => ({ ...x, address: e.target.value }))} />
      {dirty ? <button type="button" className="btn sm primary" style={{ whiteSpace: 'nowrap' }} onClick={save}>💾 存回資料庫</button> : null}
    </div>
  );
}

function AddRemarkTpl({ onAdded }) {
  const { toast } = useToast();
  const [text, setText] = useState('');
  const add = async () => {
    if (!text.trim()) { toast('請先輸入條款內容', 'err'); return; }
    try {
      const t = text.trim();
      await api.post('/remark-templates', { text: t });
      setText('');
      toast('條款已加入範本庫並套用 ✔');
      onAdded(t);
    } catch (e) { toast(e.message, 'err'); }
  };
  return (
    <div className="row mt" style={{ alignItems: 'center' }}>
      <input className="input" style={{ flex: 1 }} placeholder="新增自訂保障條款 (會存入範本庫並即時套用)" value={text}
        onChange={e => setText(e.target.value)} onKeyDown={e => e.key === 'Enter' && add()} />
      <button className="btn sm primary" disabled={!text.trim()} onClick={add}>➕ 加入並套用</button>
    </div>
  );
}

function ConvertModal({ q, data, setData, terms, settings, onClose, onDone }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setData(d => ({ ...d, [k]: v }));
  const subtotal = data.items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unit_price) || 0), 0);
  const discount = data.discount_type === 'percent' ? r2(subtotal * (Number(data.discount_value) || 0) / 100)
    : data.discount_type === 'amount' ? r2(Number(data.discount_value) || 0) : 0;
  const total = r2(subtotal - Math.min(discount, subtotal));
  const updItem = (i, k, v) => setData(d => ({ ...d, items: d.items.map((it, x) => x === i ? { ...it, [k]: v } : it) }));

  const go = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/quotations/${q.id}/convert`, {
        order_date: data.order_date, completion_date: data.completion_date,
        discount_type: data.discount_type, discount_value: Number(data.discount_value) || 0,
        payment_term_id: Number(data.payment_term_id) || null, total, subtotal,
        items: data.items.filter(it => it.description.trim()),
        remarks: data.remarks, notes: data.notes, title: q.title, category: q.category,
        customer_id: q.customer_id, address_id: q.address_id,
      });
      toast(`已生成訂單 ${r.order_no} ✔`);
      onDone(r.order_id);
    } catch (e) { toast(e.message, 'err'); } finally { setBusy(false); }
  };

  return (
    <Modal size="xl" onClose={onClose} icon="🏗️" title={`轉為工程訂單 — ${q.quote_no}`}
      footer={<>
        <button className="btn" onClick={onClose}>取消</button>
        <button className="btn violet" disabled={busy} onClick={go}>🏗️ 確認生成訂單 {total > 0 ? `(HK$ ${money(total)})` : ''}</button>
      </>}>
      <div className="form-grid mb">
        <Field label="訂單日期"><input className="input" type="date" value={data.order_date} onChange={e => set('order_date', e.target.value)} /></Field>
        <Field label="預計完工日 (可留空)"><input className="input" type="date" value={data.completion_date} onChange={e => set('completion_date', e.target.value)} /></Field>
        <Field label="折扣">
          <select className="select" value={data.discount_type} onChange={e => set('discount_type', e.target.value)}>
            <option value="none">無折扣</option><option value="percent">百分比 %</option><option value="amount">固定金額 $</option>
          </select>
        </Field>
        {data.discount_type !== 'none' ? (
          <Field label="折扣值"><input className="input num-input" type="number" step="0.01" value={data.discount_value} onChange={e => set('discount_value', e.target.value)} /></Field>
        ) : null}
        <Field label="付款條款 (發票會跟此條款開出)" span2>
          <select className="select" value={data.payment_term_id} onChange={e => set('payment_term_id', e.target.value)}>
            <option value="">— 選擇 —</option>
            {terms.map(t => <option key={t.id} value={t.id}>{t.name} — {t.description}</option>)}
          </select>
        </Field>
      </div>
      <div className="tbl-wrap">
        <table className="tbl items-table">
          <thead><tr><th>描述 (可修改)</th><th className="num" style={{ width: 70 }}>數量</th><th style={{ width: 76 }}>單位</th><th className="num" style={{ width: 100 }}>單價</th><th className="num" style={{ width: 104 }}>金額</th></tr></thead>
          <tbody>
            {data.items.map((it, i) => (
              <tr key={i}>
                <td><input className="input" value={it.description} onChange={e => updItem(i, 'description', e.target.value)} /></td>
                <td><input className="input num-input" type="number" value={it.qty} onChange={e => updItem(i, 'qty', e.target.value)} /></td>
                <td><input className="input" value={it.unit} onChange={e => updItem(i, 'unit', e.target.value)} /></td>
                <td><input className="input num-input" type="number" step="0.01" value={it.unit_price} onChange={e => updItem(i, 'unit_price', e.target.value)} /></td>
                <td className="num strong">{money((Number(it.qty) || 0) * (Number(it.unit_price) || 0))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="total-bar">
        <div className="line"><span>小計</span><span className="v">HK$ {money(subtotal)}</span></div>
        {discount > 0 ? <div className="line"><span>折扣</span><span className="v">- HK$ {money(Math.min(discount, subtotal))}</span></div> : null}
        <div className="line grand"><span>訂單總額 TOTAL AMOUNT</span><span className="v">HK$ {money(total)}</span></div>
      </div>
    </Modal>
  );
}

/* ===== 附件 PDF 檢視器 — pdf.js 渲染, 下方左右翻頁 ===== */
function PdfViewer({ pdf, onClose }) {
  const canvasRef = useRef(null);
  const [doc, setDoc] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [err, setErr] = useState('');
  const isImage = pdf.mime && pdf.mime.startsWith('image/');

  useEffect(() => {
    if (isImage) return;
    let cancelled = false; let d = null;
    (async () => {
      try {
        d = await pdfjsLib.getDocument({ url: pdf.url }).promise;
        if (cancelled) { d.destroy(); return; }
        setDoc(d); setNumPages(d.numPages); setPage(1); setErr('');
      } catch (e) { if (!cancelled) setErr('PDF 載入失敗: ' + e.message); }
    })();
    return () => { cancelled = true; if (d) d.destroy(); };
  }, [pdf.url]);

  useEffect(() => {
    if (isImage || !doc || !canvasRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const pg = await doc.getPage(page);
        if (cancelled || !canvasRef.current) return;
        const base = pg.getViewport({ scale: 1 });
        const scale = Math.min(1.6, 760 / base.width);
        const vp = pg.getViewport({ scale });
        const canvas = canvasRef.current;
        canvas.width = vp.width; canvas.height = vp.height;
        await pg.render({ canvas, canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
      } catch (e) { if (!cancelled) setErr('頁面渲染失敗: ' + e.message); }
    })();
    return () => { cancelled = true; };
  }, [doc, page]);

  return (
    <div className="modal-mask" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal xl">
        <div className="modal-head">
          <span className="ico-badge">📎</span>
          <div className="modal-title">{pdf.filename}</div>
          <a className="btn sm" href={pdf.url} download={pdf.filename}>⬇ 下載</a>
          <button className="btn" onClick={onClose}>✕ 關閉</button>
        </div>
        <div className="modal-body" style={{ background: '#3a3f4b', padding: 16, overflow: 'auto', textAlign: 'center' }}>
          {err ? <div style={{ color: '#ff9c9c', padding: 30 }}>{err}</div> : null}
          {isImage ? (
            <img src={pdf.url} alt={pdf.filename} style={{ maxWidth: '100%', boxShadow: '0 10px 30px rgba(0,0,0,.4)', background: '#fff' }} />
          ) : (
            <canvas ref={canvasRef} style={{ background: '#fff', boxShadow: '0 10px 30px rgba(0,0,0,.4)', maxWidth: '100%' }} />
          )}
        </div>
        {!isImage && numPages > 0 ? (
          <div className="row" style={{ justifyContent: 'center', gap: 18, padding: '12px 16px', background: 'var(--surface-soft)', borderRadius: '0 0 16px 16px' }}>
            <button className="btn" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>◀ 上一頁</button>
            <strong style={{ fontSize: 14, paddingTop: 8 }}>第 {page} / {numPages} 頁</strong>
            <button className="btn" disabled={page >= numPages} onClick={() => setPage(p => Math.min(numPages, p + 1))}>下一頁 ▶</button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
